/**
 * Site dashboard rail — shared context sidebar content for dashboard routes.
 *
 * Keeps the site identity, unit summary, command controls, and media section
 * consistent anywhere SiteDashboardShell is used. The shell owns layout and
 * rail styling; this component owns the ordered rail content.
 *
 * Contains:
 * - SiteDashboardRail — structure/unit/actions/media rail content (default export)
 */
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import MediaGalleryCard from '@sophocles/views/sites/components/SiteDashboard/MediaGalleryCard';
import SiteQuickActions from '@sophocles/views/sites/components/SiteDashboard/SiteQuickActions';
import StructureCard from '@sophocles/views/sites/components/StructureCard/StructureCard';
import UnitCard from '@sophocles/views/sites/components/UnitCard/UnitCard';

const roundedControlSx = { borderRadius: 'var(--button-radius)' };
const roundedTileSx = { borderRadius: 'var(--radius-sm)' };

function RailHeaderSkeleton({ titleWidth = '62%', subtitleWidth = '46%', showAction = false }) {
  const showSubtitle = subtitleWidth != null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1,
        px: 'var(--space-3)',
        pt: 'var(--space-2)',
        pb: 'var(--space-1)',
      }}
    >
      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton variant="text" width={titleWidth} sx={{ fontSize: '0.95rem' }} />
        {showSubtitle ? <Skeleton variant="text" width={subtitleWidth} sx={{ fontSize: '0.8rem' }} /> : null}
      </Stack>
      {showAction ? <Skeleton variant="rounded" width={56} height={24} sx={roundedControlSx} /> : null}
    </Box>
  );
}

RailHeaderSkeleton.propTypes = {
  titleWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  subtitleWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showAction: PropTypes.bool,
};

function DetailRowSkeleton({ labelWidth = 72, valueWidth = '64%', action = false, dense = false }) {
  if (dense) {
    return (
      <Box sx={{ minWidth: 0 }}>
        <Skeleton variant="text" width={labelWidth} sx={{ fontSize: '0.75rem' }} />
        <Skeleton variant="text" width={valueWidth} sx={{ fontSize: '0.875rem' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 'var(--space-3)',
        py: 'var(--space-2)',
        minHeight: 44,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton variant="text" width={labelWidth} sx={{ fontSize: '0.75rem' }} />
        <Skeleton variant="text" width={valueWidth} sx={{ fontSize: '0.875rem' }} />
      </Box>
      {action ? <Skeleton variant="circular" width={28} height={28} /> : null}
    </Box>
  );
}

DetailRowSkeleton.propTypes = {
  labelWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  valueWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  action: PropTypes.bool,
  dense: PropTypes.bool,
};

function SignalBarsSkeleton() {
  return (
    <Stack direction="row" spacing={0.25} alignItems="flex-end" sx={{ height: 18 }}>
      {[7, 10, 13, 16].map(height => (
        <Skeleton key={`rail-signal-${height}`} variant="rounded" width={5} height={height} sx={roundedTileSx} />
      ))}
    </Stack>
  );
}

function StructureRailSkeleton() {
  return (
    <Box sx={{ width: '100%' }}>
      <RailHeaderSkeleton titleWidth="70%" subtitleWidth="54%" showAction />
      {/* Flat, like the real rail card (BasicDashboardCardRail has no card
          surface) — a tinted/bordered box here read as a white panel against
          the --surface-secondary sidebar. */}
      <Box sx={{ mx: 'var(--space-3)', my: 'var(--space-1)' }}>
        <DetailRowSkeleton labelWidth={64} valueWidth="72%" />
        <DetailRowSkeleton labelWidth={76} valueWidth="42%" />
        <DetailRowSkeleton labelWidth={112} valueWidth="74%" action />
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 'var(--space-2)',
          px: 'var(--space-3)',
          pt: 'var(--space-1)',
          pb: 'var(--space-2)',
        }}
      >
        <DetailRowSkeleton dense labelWidth={82} valueWidth="72%" />
        <DetailRowSkeleton dense labelWidth={96} valueWidth="64%" />
      </Box>
    </Box>
  );
}

function UnitRailSkeleton() {
  return (
    <Box sx={{ px: 'var(--space-3)', py: 'var(--space-2)' }}>
      <Stack spacing={1.25} sx={{ minWidth: 0 }}>
        <Box sx={{ minWidth: 0, position: 'relative', pr: 5 }}>
          <Skeleton
            variant="rounded"
            width={36}
            height={32}
            sx={{ ...roundedControlSx, position: 'absolute', top: 0, right: 0 }}
          />
          <Skeleton variant="text" width="58%" sx={{ fontSize: '0.875rem' }} />
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.5 }}>
            <Skeleton variant="text" width="48%" sx={{ fontSize: '0.75rem' }} />
            {[0, 1, 2].map(index => (
              <Skeleton key={`unit-action-${index}`} variant="circular" width={22} height={22} />
            ))}
          </Stack>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
            gap: 'var(--space-2)',
          }}
        >
          <DetailRowSkeleton dense labelWidth={74} valueWidth="68%" />
          <Box
            sx={{ minWidth: 0, justifySelf: 'end', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}
          >
            <Skeleton variant="text" width={48} sx={{ fontSize: '0.75rem' }} />
            <SignalBarsSkeleton />
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}

function CommandRailSkeleton() {
  return (
    <Box sx={{ px: 'var(--space-2)', py: 'var(--space-2)' }}>
      <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" useFlexGap>
        {[0, 1, 2, 3].map(index => (
          <Skeleton
            key={`quick-action-skeleton-${index}`}
            variant="rounded"
            width="9.5rem"
            height={32}
            sx={roundedControlSx}
          />
        ))}
      </Stack>
    </Box>
  );
}

function MediaRailSkeleton() {
  return (
    <Box sx={{ width: '100%' }}>
      <RailHeaderSkeleton titleWidth={72} subtitleWidth={null} showAction />
      <Box sx={{ px: 'var(--space-3)', pb: 'var(--space-2)' }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 'var(--space-2)' }}>
          {[0, 1, 2, 3].map(index => (
            <Skeleton key={`media-thumb-${index}`} variant="rounded" width={56} height={56} sx={roundedTileSx} />
          ))}
        </Stack>
        <Skeleton variant="text" width="44%" sx={{ fontSize: '0.75rem' }} />
      </Box>
    </Box>
  );
}

function SiteDashboardRailSkeleton() {
  return (
    <>
      <StructureRailSkeleton />
      <Divider sx={{ my: 1 }} />
      <UnitRailSkeleton />
      <Divider sx={{ my: 1 }} />
      <CommandRailSkeleton />
      <Divider sx={{ my: 1 }} />
      <MediaRailSkeleton />
    </>
  );
}

function SiteDashboardRail({
  loading = false,
  isBillboard,
  location,
  onMobile,
  params,
  siteId,
  site,
  siteStatus,
  unit,
  weatherConditions,
  showStructureUnitDivider = false,
  commandUnitId = null,
  canSendCommand = false,
  moreCommandsAction = null,
  commandLogPath = null,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const showCommandArea = loading || commandUnitId != null || moreCommandsAction;

  if (loading) {
    return <SiteDashboardRailSkeleton />;
  }

  return (
    <>
      <StructureCard
        loading={loading}
        isBillboard={isBillboard}
        location={location}
        onMobile={onMobile}
        site={site}
        status={siteStatus}
        weatherConditions={weatherConditions}
      />
      {showStructureUnitDivider && <Divider sx={{ my: 1 }} />}
      <UnitCard
        loading={loading}
        params={params}
        isBillboard={isBillboard}
        location={location}
        onMobile={onMobile}
        shouldFetchStatus={false}
        siteId={siteId}
        site={site}
        unit={unit}
      />
      {showCommandArea ? (
        <>
          <Divider sx={{ my: 1 }} />
          {/* pb matches the buttons' effective side inset (--space-3 card
              padding) so the surface's bottom edge doesn't read tighter than
              its sides when this is the last rail section. */}
          <Box sx={{ px: 'var(--space-2)', pt: 'var(--space-2)', pb: 'var(--space-3)' }}>
            {loading ? (
              <Stack direction="row" spacing={1} justifyContent="center">
                {[0, 1, 2].map(index => (
                  <Skeleton key={`quick-action-skeleton-${index}`} variant="rounded" width={44} height={36} />
                ))}
              </Stack>
            ) : (
              <>
                {commandUnitId != null ? (
                  <SiteQuickActions
                    siteId={siteId}
                    canSendCommand={canSendCommand}
                    appendActions={moreCommandsAction}
                  />
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>{moreCommandsAction}</Box>
                )}
                {commandLogPath ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 'var(--space-1)' }}>
                    <Button
                      size="small"
                      endIcon={<ChevronRightIcon fontSize="small" />}
                      onClick={() => navigate(commandLogPath)}
                    >
                      {t('sites.recentCommands', 'Recent Commands')}
                    </Button>
                  </Box>
                ) : null}
              </>
            )}
          </Box>
        </>
      ) : null}
      <MediaGalleryCard loading={loading} onMobile={onMobile} params={params} />
    </>
  );
}

SiteDashboardRail.propTypes = {
  loading: PropTypes.bool,
  isBillboard: PropTypes.bool,
  location: PropTypes.object,
  onMobile: PropTypes.bool,
  params: PropTypes.shape({
    cust_id: PropTypes.string,
    sitegroup_id: PropTypes.string,
    site_id: PropTypes.string,
  }).isRequired,
  siteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  site: PropTypes.object,
  /** Site grid-style status token driving the structure card's header dot. */
  siteStatus: PropTypes.oneOf(['good', 'bad', 'warning', 'purple', 'info', 'disabled']),
  unit: PropTypes.object,
  weatherConditions: PropTypes.object,
  showStructureUnitDivider: PropTypes.bool,
  commandUnitId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  canSendCommand: PropTypes.bool,
  moreCommandsAction: PropTypes.node,
  commandLogPath: PropTypes.string,
};

export default SiteDashboardRail;
